import { Injectable } from '@angular/core';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { StaffTimecard } from '../models/staff-timecard.model';

// Initialize pdfMake with fonts (matching project pattern)
(pdfMake as any).default.vfs = (pdfFonts as any).default.vfs;

export interface TimecardReportData {
  companyName: string;
  branchName: string;
  fromDate: string;
  toDate: string;
  generatedDate: string;
  timecards: StaffTimecard[];
  staffFilter?: string; // 'All Staff' or specific staff name
}

export interface StaffSummary {
  staffName: string;
  totalDays: number;
  totalHours: number;
  scheduledHours: number;
}

@Injectable({
  providedIn: 'root'
})
export class TimecardPdfReportService {

  constructor() { }

  /**
   * Generate and download the timecard PDF report
   */
  generateReport(data: TimecardReportData): void {
    const docDefinition = this.buildDocDefinition(data);
    pdfMake.createPdf(docDefinition).download(`Timecard_Report_${data.fromDate}_to_${data.toDate}.pdf`);
  }

  /**
   * Generate and open the PDF in a new tab
   */
  openReport(data: TimecardReportData): void {
    const docDefinition = this.buildDocDefinition(data);
    pdfMake.createPdf(docDefinition).open();
  }

  /**
   * Build the PDF document definition
   */
  private buildDocDefinition(data: TimecardReportData): any {
    // Group timecards by staff
    const staffGroups = this.groupByStaff(data.timecards);

    // Calculate summaries
    const summaries = this.calculateSummaries(staffGroups);

    return {
      pageSize: 'A4',
      pageMargins: [40, 60, 40, 60],

      header: {
        columns: [
          { text: data.companyName, style: 'headerCompany', margin: [40, 20, 0, 0] },
          { text: 'Timecard Report', style: 'headerTitle', alignment: 'right', margin: [0, 20, 40, 0] }
        ]
      },

      footer: (currentPage: number, pageCount: number) => ({
        columns: [
          { text: `Generated: ${data.generatedDate}`, style: 'footer', margin: [40, 0, 0, 0] },
          { text: `Page ${currentPage} of ${pageCount}`, style: 'footer', alignment: 'right', margin: [0, 0, 40, 0] }
        ]
      }),

      content: [
        // Report Header Info
        {
          columns: [
            {
              width: '*',
              stack: [
                { text: `Branch: ${data.branchName}`, style: 'subheader' },
                { text: `Period: ${this.formatDisplayDate(data.fromDate)} - ${this.formatDisplayDate(data.toDate)}`, style: 'subheader' },
                { text: `Staff: ${data.staffFilter || 'All Staff'}`, style: 'subheader' }
              ]
            }
          ],
          margin: [0, 0, 0, 20]
        },

        // Summary Section
        { text: 'Summary', style: 'sectionHeader' },
        this.buildSummaryTable(summaries),
        { text: '', margin: [0, 0, 0, 20] },

        // Detailed Section
        { text: 'Detailed Records', style: 'sectionHeader' },
        ...this.buildDetailedSections(staffGroups)
      ],

      styles: {
        headerCompany: {
          fontSize: 14,
          bold: true,
          color: '#333333'
        },
        headerTitle: {
          fontSize: 14,
          bold: true,
          color: '#666666'
        },
        subheader: {
          fontSize: 10,
          color: '#666666',
          margin: [0, 2, 0, 2]
        },
        sectionHeader: {
          fontSize: 12,
          bold: true,
          color: '#333333',
          margin: [0, 10, 0, 10],
          decoration: 'underline'
        },
        staffHeader: {
          fontSize: 11,
          bold: true,
          color: '#1a73e8',
          margin: [0, 15, 0, 5]
        },
        tableHeader: {
          fontSize: 9,
          bold: true,
          fillColor: '#f5f5f5',
          color: '#333333'
        },
        tableCell: {
          fontSize: 9,
          color: '#333333'
        },
        tableCellRight: {
          fontSize: 9,
          color: '#333333',
          alignment: 'right'
        },
        tableCellCenter: {
          fontSize: 9,
          color: '#333333',
          alignment: 'center'
        },
        totalRow: {
          fontSize: 9,
          bold: true,
          fillColor: '#e8f0fe',
          color: '#333333'
        },
        footer: {
          fontSize: 8,
          color: '#999999'
        }
      },

      defaultStyle: {
        font: 'Roboto'
      }
    };
  }

  /**
   * Group timecards by staff member
   */
  private groupByStaff(timecards: StaffTimecard[]): Map<string, StaffTimecard[]> {
    const groups = new Map<string, StaffTimecard[]>();

    timecards.forEach(tc => {
      const staffName = tc.staffName || `${tc.firstName} ${tc.lastName}`;
      if (!groups.has(staffName)) {
        groups.set(staffName, []);
      }
      groups.get(staffName)!.push(tc);
    });

    // Sort each group by date
    groups.forEach((records, staffName) => {
      records.sort((a, b) => new Date(a.workDate).getTime() - new Date(b.workDate).getTime());
    });

    return groups;
  }

  /**
   * Calculate summary data for each staff member
   */
  private calculateSummaries(staffGroups: Map<string, StaffTimecard[]>): StaffSummary[] {
    const summaries: StaffSummary[] = [];

    staffGroups.forEach((records, staffName) => {
      const totalHours = records.reduce((sum, tc) => sum + (tc.totalHours || 0), 0);
      const scheduledHours = records.reduce((sum, tc) => {
        if (tc.scheduledStartTime && tc.scheduledEndTime) {
          const start = this.timeStringToHours(tc.scheduledStartTime);
          const end = this.timeStringToHours(tc.scheduledEndTime);
          return sum + (end - start);
        }
        return sum;
      }, 0);

      summaries.push({
        staffName,
        totalDays: records.length,
        totalHours: Math.round(totalHours * 100) / 100,
        scheduledHours: Math.round(scheduledHours * 100) / 100
      });
    });

    // Sort by staff name
    summaries.sort((a, b) => a.staffName.localeCompare(b.staffName));
    return summaries;
  }

  /**
   * Build the summary table
   */
  private buildSummaryTable(summaries: StaffSummary[]): any {
    const totalDays = summaries.reduce((sum, s) => sum + s.totalDays, 0);
    const totalHours = summaries.reduce((sum, s) => sum + s.totalHours, 0);
    const totalScheduled = summaries.reduce((sum, s) => sum + s.scheduledHours, 0);

    return {
      table: {
        headerRows: 1,
        widths: ['*', 'auto', 'auto', 'auto', 'auto'],
        body: [
          // Header row
          [
            { text: 'Staff Name', style: 'tableHeader' },
            { text: 'Days Worked', style: 'tableHeader', alignment: 'center' },
            { text: 'Scheduled Hours', style: 'tableHeader', alignment: 'right' },
            { text: 'Actual Hours', style: 'tableHeader', alignment: 'right' },
            { text: 'Difference', style: 'tableHeader', alignment: 'right' }
          ],
          // Data rows
          ...summaries.map(s => [
            { text: s.staffName, style: 'tableCell' },
            { text: s.totalDays.toString(), style: 'tableCellCenter' },
            { text: s.scheduledHours.toFixed(2), style: 'tableCellRight' },
            { text: s.totalHours.toFixed(2), style: 'tableCellRight' },
            { text: (s.totalHours - s.scheduledHours).toFixed(2), style: 'tableCellRight' }
          ]),
          // Total row
          [
            { text: 'TOTAL', style: 'totalRow' },
            { text: totalDays.toString(), style: 'totalRow', alignment: 'center' },
            { text: totalScheduled.toFixed(2), style: 'totalRow', alignment: 'right' },
            { text: totalHours.toFixed(2), style: 'totalRow', alignment: 'right' },
            { text: (totalHours - totalScheduled).toFixed(2), style: 'totalRow', alignment: 'right' }
          ]
        ]
      },
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => '#dddddd',
        vLineColor: () => '#dddddd',
        paddingLeft: () => 8,
        paddingRight: () => 8,
        paddingTop: () => 5,
        paddingBottom: () => 5
      }
    };
  }

  /**
   * Build detailed sections for each staff member
   */
  private buildDetailedSections(staffGroups: Map<string, StaffTimecard[]>): any[] {
    const sections: any[] = [];

    // Sort staff names
    const sortedStaffNames = Array.from(staffGroups.keys()).sort();

    sortedStaffNames.forEach(staffName => {
      const records = staffGroups.get(staffName)!;

      // Staff header
      sections.push({ text: staffName, style: 'staffHeader' });

      // Staff detail table
      sections.push(this.buildStaffDetailTable(records));
    });

    return sections;
  }

  /**
   * Build detail table for a single staff member
   */
  private buildStaffDetailTable(records: StaffTimecard[]): any {
    const totalHours = records.reduce((sum, tc) => sum + (tc.totalHours || 0), 0);

    return {
      table: {
        headerRows: 1,
        widths: ['auto', 'auto', 'auto', 'auto', 'auto', '*'],
        body: [
          // Header row
          [
            { text: 'Date', style: 'tableHeader' },
            { text: 'Scheduled', style: 'tableHeader' },
            { text: 'Clock In', style: 'tableHeader' },
            { text: 'Clock Out', style: 'tableHeader' },
            { text: 'Hours', style: 'tableHeader', alignment: 'right' },
            { text: 'Notes', style: 'tableHeader' }
          ],
          // Data rows
          ...records.map(tc => [
            { text: this.formatDisplayDate(tc.workDate), style: 'tableCell' },
            { text: this.formatScheduledRange(tc.scheduledStartTime, tc.scheduledEndTime), style: 'tableCell' },
            { text: this.formatTime(tc.clockInTime), style: 'tableCell' },
            { text: this.formatTime(tc.clockOutTime), style: 'tableCell' },
            { text: tc.totalHours?.toFixed(2) || '-', style: 'tableCellRight' },
            { text: tc.notes || '', style: 'tableCell' }
          ]),
          // Total row for this staff
          [
            { text: 'Total', style: 'totalRow', colSpan: 4 },
            {}, {}, {},
            { text: totalHours.toFixed(2), style: 'totalRow', alignment: 'right' },
            { text: '', style: 'totalRow' }
          ]
        ]
      },
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => '#dddddd',
        vLineColor: () => '#dddddd',
        paddingLeft: () => 6,
        paddingRight: () => 6,
        paddingTop: () => 4,
        paddingBottom: () => 4
      },
      margin: [0, 0, 0, 10]
    };
  }

  /**
   * Format date for display (e.g., "Jan 15, 2026")
   */
  private formatDisplayDate(date: Date | string | undefined): string {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  /**
   * Format time for display (e.g., "9:00 AM")
   */
  private formatTime(dateTime: Date | string | undefined): string {
    if (!dateTime) return '-';
    const d = new Date(dateTime);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  /**
   * Format scheduled time range (e.g., "8:00 AM - 5:00 PM")
   */
  private formatScheduledRange(start: string | undefined, end: string | undefined): string {
    if (!start || !end) return '-';
    return `${this.formatTimeString(start)} - ${this.formatTimeString(end)}`;
  }

  /**
   * Format time string "HH:mm:ss" to display format
   */
  private formatTimeString(timeStr: string | undefined): string {
    if (!timeStr) return '-';
    const parts = timeStr.split(':');
    if (parts.length < 2) return timeStr;

    const hours = parseInt(parts[0], 10);
    const minutes = parts[1];
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes} ${ampm}`;
  }

  /**
   * Convert time string "HH:mm:ss" to decimal hours
   */
  private timeStringToHours(timeStr: string): number {
    if (!timeStr) return 0;
    const parts = timeStr.split(':');
    if (parts.length < 2) return 0;
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    return hours + (minutes / 60);
  }
}
